import type { Locale } from '../i18n'
import type { Account, DirectoryCount, RuntimeStatus } from '../types'

export function formatTimestamp(value: string | undefined, locale: Locale): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function accountInitials(displayName: string): string {
  const name = displayName.trim()
  if (!name) return '—'
  const parts = name.split(/\s+/).filter(Boolean)
  return parts.length > 1
    ? parts.map((part) => part[0]).join('').slice(0, 2).toUpperCase()
    : (Array.from(name)[0] ?? '—').toUpperCase()
}

export function greetingName(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  return parts[0] || displayName
}

export function accountStatusLabel(status: Account['status'], locale: Locale): string {
  const labels: Record<Account['status'], Record<Locale, string>> = {
    active: { 'zh-CN': '账户状态正常', 'en-US': 'Account is in good standing' },
    pending_deletion: { 'zh-CN': '账户待删除', 'en-US': 'Account deletion pending' },
    recovery: { 'zh-CN': '账户恢复中', 'en-US': 'Account recovery in progress' },
  }
  return labels[status][locale]
}

export function runtimeConnectionLabel(statuses: RuntimeStatus[], locale: Locale, exact = true): string {
  if (!statuses.length) return locale === 'zh-CN' ? '暂无运行环境' : 'No Runtimes yet'
  if (statuses.every((status) => status === 'online')) {
    if (!exact) return locale === 'zh-CN' ? '已加载环境均在线' : 'Loaded Runtimes are online'
    return locale === 'zh-CN' ? '环境均在线' : 'All Runtimes online'
  }
  if (!exact) return locale === 'zh-CN' ? '已加载环境中有需关注项' : 'Some loaded Runtimes need attention'
  return locale === 'zh-CN' ? '部分环境需关注' : 'Some Runtimes need attention'
}

export function runtimeSignalTone(statuses: RuntimeStatus[], exact: boolean): 'online' | 'attention' | 'unknown' {
  if (!statuses.length) return 'unknown'
  if (statuses.some((status) => status !== 'online')) return 'attention'
  return exact ? 'online' : 'unknown'
}

export function directoryCountLabel(count: DirectoryCount): string {
  return `${count.value}${count.exact ? '' : '+'}`
}
